import { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { FiSearch, FiRepeat, FiCheckCircle, FiAlertTriangle } from "react-icons/fi";
import libraryService from "../../services/libraryService";
import studentService from "../../services/studentService";

// Works whether Student.js stores a single `fullName` or split `firstName`/`lastName`.
const getStudentName = (student) => {
    if (!student) return "Unknown Student";
    return (
        student.fullName ||
        `${student.firstName || ""} ${student.lastName || ""}`.trim() ||
        "Unknown Student"
    );
};

const STATUS_FILTERS = ["All", "Issued", "Overdue", "Returned"];

function StudentPicker({ selected, onSelect }) {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const boxRef = useRef(null);

    useEffect(() => {
        if (!query.trim()) {
            setResults([]);
            return;
        }
        setLoading(true);
        const timer = setTimeout(async () => {
            try {
                const res = await studentService.getAll({ search: query, limit: 8 });
                setResults(res.data.students || res.data.data || res.data || []);
            } catch (err) {
                toast.error(err.message);
            } finally {
                setLoading(false);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [query]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div ref={boxRef} style={{ position: "relative" }}>
            <label className="form-label">Student</label>
            {selected ? (
                <div
                    className="student-select-box"
                    style={{ padding: "0.6rem 0.75rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                >
                    <span style={{ fontSize: 13.5 }}>
                        <strong>{getStudentName(selected)}</strong>
                        {selected.rollNumber ? ` · Roll No. ${selected.rollNumber}` : ""}
                    </span>
                    <button type="button" className="btn btn-outline btn-sm" onClick={() => onSelect(null)}>
                        Change
                    </button>
                </div>
            ) : (
                <div className="student-select-box">
                    <div className="student-select-search">
                        <FiSearch size={14} color="var(--text-muted)" />
                        <input
                            placeholder="Search student by name or roll no..."
                            value={query}
                            onFocus={() => setOpen(true)}
                            onChange={(e) => {
                                setQuery(e.target.value);
                                setOpen(true);
                            }}
                        />
                    </div>
                    {open && (
                        <div className="student-select-list">
                            {loading && (
                                <div style={{ padding: "0.6rem 0.75rem", fontSize: 12.5, color: "var(--text-muted)" }}>
                                    Searching...
                                </div>
                            )}
                            {!loading && query.trim() && results.length === 0 && (
                                <div style={{ padding: "0.6rem 0.75rem", fontSize: 12.5, color: "var(--text-muted)" }}>
                                    No students found.
                                </div>
                            )}
                            {results.map((student) => (
                                <div
                                    key={student._id}
                                    className="student-select-item"
                                    onClick={() => {
                                        onSelect(student);
                                        setOpen(false);
                                        setQuery("");
                                    }}
                                >
                                    {getStudentName(student)}
                                    {student.rollNumber ? ` · Roll No. ${student.rollNumber}` : ""}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

function BookPicker({ selected, onSelect }) {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const boxRef = useRef(null);

    useEffect(() => {
        if (!query.trim()) {
            setResults([]);
            return;
        }
        setLoading(true);
        const timer = setTimeout(async () => {
            try {
                const res = await libraryService.getBooks({ search: query, limit: 8 });
                setResults(res.data.books || []);
            } catch (err) {
                toast.error(err.message);
            } finally {
                setLoading(false);
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [query]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div ref={boxRef} style={{ position: "relative" }}>
            <label className="form-label">Book</label>
            {selected ? (
                <div
                    className="student-select-box"
                    style={{ padding: "0.6rem 0.75rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                >
                    <span style={{ fontSize: 13.5 }}>
                        <strong>{selected.title}</strong> · {selected.author}
                        <span style={{ color: "var(--text-muted)" }}> ({selected.availableCopies} available)</span>
                    </span>
                    <button type="button" className="btn btn-outline btn-sm" onClick={() => onSelect(null)}>
                        Change
                    </button>
                </div>
            ) : (
                <div className="student-select-box">
                    <div className="student-select-search">
                        <FiSearch size={14} color="var(--text-muted)" />
                        <input
                            placeholder="Search book by title, author, or ISBN..."
                            value={query}
                            onFocus={() => setOpen(true)}
                            onChange={(e) => {
                                setQuery(e.target.value);
                                setOpen(true);
                            }}
                        />
                    </div>
                    {open && (
                        <div className="student-select-list">
                            {loading && (
                                <div style={{ padding: "0.6rem 0.75rem", fontSize: 12.5, color: "var(--text-muted)" }}>
                                    Searching...
                                </div>
                            )}
                            {!loading && query.trim() && results.length === 0 && (
                                <div style={{ padding: "0.6rem 0.75rem", fontSize: 12.5, color: "var(--text-muted)" }}>
                                    No books found.
                                </div>
                            )}
                            {results.map((book) => (
                                <div
                                    key={book._id}
                                    className="student-select-item"
                                    style={book.availableCopies < 1 ? { opacity: 0.5, cursor: "not-allowed" } : {}}
                                    onClick={() => {
                                        if (book.availableCopies < 1) {
                                            toast.error("No copies available for this book.");
                                            return;
                                        }
                                        onSelect(book);
                                        setOpen(false);
                                        setQuery("");
                                    }}
                                >
                                    {book.title} · {book.author}
                                    <span style={{ color: "var(--text-muted)" }}> ({book.availableCopies} available)</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

export default function IssueReturn() {
    const [student, setStudent] = useState(null);
    const [book, setBook] = useState(null);
    const [issuing, setIssuing] = useState(false);

    const [issues, setIssues] = useState([]);
    const [statusFilter, setStatusFilter] = useState("All");
    const [search, setSearch] = useState("");
    const [loading, setLoading] = useState(true);
    const [returningId, setReturningId] = useState(null);

    const fetchIssues = async () => {
        setLoading(true);
        try {
            const params = { search };
            if (statusFilter !== "All" && statusFilter !== "Overdue") params.status = statusFilter;
            if (statusFilter === "Overdue") params.status = "Issued";
            const res = await libraryService.getIssues(params);
            let list = res.data.issues || [];
            if (statusFilter === "Overdue") {
                list = list.filter((i) => new Date(i.dueDate) < new Date());
            }
            setIssues(list);
        } catch (err) {
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchIssues();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [statusFilter]);

    useEffect(() => {
        const timer = setTimeout(fetchIssues, 350);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    const handleIssue = async (e) => {
        e.preventDefault();
        if (!student || !book) {
            toast.error("Select both a student and a book.");
            return;
        }
        setIssuing(true);
        try {
            await libraryService.issueBook({ studentId: student._id, bookId: book._id });
            toast.success(`"${book.title}" issued to ${getStudentName(student)}.`);
            setStudent(null);
            setBook(null);
            fetchIssues();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message);
        } finally {
            setIssuing(false);
        }
    };

    const handleReturn = async (issue) => {
        setReturningId(issue._id);
        try {
            const res = await libraryService.returnBook(issue._id);
            const fine = res.data.fineAmount;
            if (fine > 0) {
                toast.success(`Returned. Late fine of ₹${fine} applies — see Pending Fines to collect payment.`);
            } else {
                toast.success("Book returned — no fine due.");
            }
            fetchIssues();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message);
        } finally {
            setReturningId(null);
        }
    };

    const isOverdue = (issue) => issue.status === "Issued" && new Date(issue.dueDate) < new Date();

    return (
        <div>
            <div className="page-header">
                <div>
                    <h1 className="page-title">Issue / Return</h1>
                    <p className="page-subtitle">Issue books to students and process returns.</p>
                </div>
            </div>

            <div className="card" style={{ marginBottom: "1.25rem" }}>
                <div className="card-header">
                    <strong>Issue a Book</strong>
                </div>
                <div className="card-body">
                    <form onSubmit={handleIssue}>
                        <div className="form-grid">
                            <div className="form-group">
                                <StudentPicker selected={student} onSelect={setStudent} />
                            </div>
                            <div className="form-group">
                                <BookPicker selected={book} onSelect={setBook} />
                            </div>
                        </div>
                        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1rem" }}>
                            <button type="submit" className="btn btn-primary" disabled={issuing || !student || !book}>
                                <FiRepeat size={14} style={{ marginRight: 6 }} />
                                {issuing ? "Issuing..." : "Issue Book"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>

            <div className="card">
                <div
                    className="card-header"
                    style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.75rem" }}
                >
                    <div style={{ display: "flex", gap: "0.4rem" }}>
                        {STATUS_FILTERS.map((f) => (
                            <button
                                key={f}
                                type="button"
                                className={`btn btn-sm ${statusFilter === f ? "btn-primary" : "btn-outline"}`}
                                onClick={() => setStatusFilter(f)}
                            >
                                {f}
                            </button>
                        ))}
                    </div>
                    <div className="search-bar" style={{ width: 240 }}>
                        <FiSearch size={14} color="var(--text-muted)" />
                        <input placeholder="Search student or book..." value={search} onChange={(e) => setSearch(e.target.value)} />
                    </div>
                </div>

                <div className="card-body" style={{ padding: 0 }}>
                    {loading ? (
                        <div className="loading-center">
                            <div className="spinner" />
                            Loading issues...
                        </div>
                    ) : issues.length === 0 ? (
                        <div className="empty-state">
                            <FiRepeat />
                            <p>No issue records match this filter.</p>
                        </div>
                    ) : (
                        <div className="table-wrapper">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Book</th>
                                        <th>Student</th>
                                        <th>Issue Date</th>
                                        <th>Due Date</th>
                                        <th>Status</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {issues.map((issue) => (
                                        <tr key={issue._id}>
                                            <td>{issue.book?.title || "—"}</td>
                                            <td>{getStudentName(issue.student)}</td>
                                            <td>{new Date(issue.issueDate).toLocaleDateString()}</td>
                                            <td>{new Date(issue.dueDate).toLocaleDateString()}</td>
                                            <td>
                                                {issue.status === "Returned" ? (
                                                    <span className="badge badge-green">Returned</span>
                                                ) : isOverdue(issue) ? (
                                                    <span className="badge" style={{ background: "#fee2e2", color: "#b91c1c" }}>
                                                        <FiAlertTriangle size={10} style={{ marginRight: 3, verticalAlign: -1 }} />
                                                        Overdue
                                                    </span>
                                                ) : (
                                                    <span className="badge badge-blue">Issued</span>
                                                )}
                                            </td>
                                            <td>
                                                {issue.status === "Issued" ? (
                                                    <button
                                                        className="btn btn-outline btn-sm"
                                                        onClick={() => handleReturn(issue)}
                                                        disabled={returningId === issue._id}
                                                    >
                                                        <FiCheckCircle size={13} style={{ marginRight: 4 }} />
                                                        {returningId === issue._id ? "Returning..." : "Return"}
                                                    </button>
                                                ) : (
                                                    <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                                                        {issue.fineAmount > 0 ? `Fine: ₹${issue.fineAmount}` : "—"}
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}